import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Portal, Transition } from '@headlessui/react';
import { useReducedMotion } from '../ui/useReducedMotion';
import { AnimatedLabel } from '../ui/AnimatedLabel';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { useFeatureLearning } from './FeatureLearningProvider';
import type { FeatureLessonOutcome } from '../../services/featureLearningService';
import './feature-learning.css';

/** Copy can evolve without changing the account's stable lesson identity. */
const LESSONS = {
  create: { heading: 'Create and share', body: 'Describe your goal. AI writes questions. Review, save and share.', action: 'Start exploring', description: 'Describe your goal, build questions, create and share the link.' },
  explore: { heading: 'Questions that adapt to each answer', body: 'Tell AI what you want to learn. It asks follow-ups based on each reply. Your goal stays private; Light, Balanced and Deep control how far it explores.', action: 'Got it', description: 'Explore offers Light, Balanced and Deep. The AI follows up on setup, then asks what happened when connecting the CRM.' },
  calls: { heading: 'The same form, answered by voice', body: 'People speak with an AI interviewer in their browser. It asks your questions and saves their answers in the same conversation. You don’t need to join.', action: 'Got it', description: 'People join a call, speak with the AI, and see their conversation. Each call uses one credit.' },
  'auto-run': { heading: 'Run saved AI instructions on new answers', body: 'A recipe is a saved set of AI instructions, such as writing a case study. Auto-run applies it after each new response and saves the result.', action: 'Got it', description: 'Auto-run applies your chosen recipe to each new submission and saves the result. One automatic recipe per campaign.' },
};

/** Each example loops independently until the reader chooses Next or Back. */
const CREATION_STEPS = [
  { heading: 'Any form you need, drafted instantly',
    body: 'Write your goals, drop in a document, or paste a link to an existing form to get started.',
    clip: 'create-draft',
    description: 'A goal is typed, a document is attached, and AI generates questions from them.' },
  { heading: 'Add new questions just by describing them',
    body: 'Drag and drop to rearrange, or add any type of question instantly ready to go.',
    clip: 'create-edit',
    description: 'Describe a new question, drag questions into a different order, or add a question from the type menu.' },
  { heading: 'Share or embed your form',
    body: 'Copy the link and send it to anyone without an account, or paste the code directly into your website for a native look.',
    clip: 'create-share',
    description: 'Copy the share link for someone to answer, then copy the embed code to show the form inside a website.' },
];

/** Reflow the lesson itself when the phone viewport cannot show readable wide UI. */
function subscribeCompact(listener: () => void) {
  const query = window.matchMedia?.('(max-width: 480px)');
  query?.addEventListener('change', listener);
  return () => query?.removeEventListener('change', listener);
}

/** Keep a stable snapshot for responsive playback and server rendering. */
const isCompact = () => window.matchMedia?.('(max-width: 480px)').matches ?? false;

/** A silent example, with a still and the same explanation for reduced motion or playback failure. */
function LessonVideo({ lesson, active, onDone, onClose, id }: { lesson: keyof typeof LESSONS; active: boolean; onDone: () => void; onClose: () => void; id: string }) {
  const compact = useSyncExternalStore(subscribeCompact, isCompact, () => false) && lesson !== 'auto-run';
  const [creationStep, setCreationStep] = useState(0);
  const step = lesson === 'create' ? CREATION_STEPS[creationStep] : undefined;
  const source = `/learning/${step?.clip ?? lesson}-dark${compact ? '-compact' : ''}`;
  const poster = `${source}.webp`;
  const copy = step ?? LESSONS[lesson];
  const action = step && creationStep < CREATION_STEPS.length - 1 ? 'Next' : LESSONS[lesson].action;
  const secondaryAction = step && creationStep > 0 ? 'Back' : 'Later';
  const reduced = useReducedMotion();
  const [failedSource, setFailedSource] = useState<string | null>(null);
  const [pageVisible, setPageVisible] = useState(document.visibilityState !== 'hidden');
  const video = useRef<HTMLVideoElement>(null);
  const [displayedSource, setDisplayedSource] = useState<string | null>(null);
  const animate = !reduced && failedSource !== source;
  const playing = animate && active && pageVisible;

  useEffect(() => {
    const update = () => setPageVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  useEffect(() => {
    const element = video.current;
    if (!element) return undefined;
    let cancelled = false;
    if (playing) {
      void element.play().catch((error: unknown) => {
        // Navigation or a responsive source change can interrupt a pending play.
        if (cancelled || (error instanceof DOMException && error.name === 'AbortError')) return;
        setFailedSource(source);
        if (!(error instanceof DOMException && error.name === 'NotAllowedError')) {
          captureException(error instanceof Error ? error : String(error), 'FeatureLesson.play', { lesson });
        }
      });
    } else element.pause();
    return () => { cancelled = true; element.pause(); };
  }, [playing, source, lesson]);

  const advance = () => {
    if (step && creationStep < CREATION_STEPS.length - 1) setCreationStep(creationStep + 1);
    else onDone();
  };

  return <>
    <div className="feature-lesson-media" style={{ aspectRatio: compact ? 1 : 2 }}>
      <img src={poster} alt={copy.description} width={compact ? 720 : 960} height={compact ? 720 : 480} />
      {animate && <video ref={video} src={`${source}.mp4`} poster={poster} muted loop playsInline preload="none"
        aria-label={copy.description} style={{ opacity: displayedSource === source ? 1 : 0 }}
        onLoadedData={() => setDisplayedSource(source)}
        onError={() => {
          setFailedSource(source);
          captureException(new Error('Learning video unavailable'), 'FeatureLesson.media', { lesson });
        }} />}
    </div>
    <div className="feature-lesson-copy" aria-live={step ? 'polite' : undefined} aria-atomic={step ? true : undefined}>
      <h3 id={`${id}-title`}><AnimatedLabel text={copy.heading} reserveText={step ? CREATION_STEPS[1].heading : undefined} multiline /></h3>
      <p id={`${id}-description`}><AnimatedLabel text={copy.body} reserveText={step ? CREATION_STEPS[2].body : undefined} multiline /></p>
    </div>
    <div className="feature-lesson-footer">
      <button type="button" className="feature-lesson-later" onClick={() => {
        if (step && creationStep > 0) setCreationStep(creationStep - 1);
        else onClose();
      }}><AnimatedLabel text={secondaryAction} reserveText="Later" /></button>
      <button type="button" className={`feature-lesson-action ${step ? 'min-w-36 border border-border bg-transparent text-foreground hover:bg-surface-hover' : 'button-accent'}`} onClick={advance}>
        <AnimatedLabel text={action} reserveText={step ? LESSONS.create.action : undefined} />
      </button>
    </div>
  </>;
}

/** One contextual card above the app, anchored to the real control without shifting it. */
export function FeatureLesson({ lesson, engaged = false, ready = true, children }: {
  lesson: keyof typeof LESSONS;
  engaged?: boolean;
  ready?: boolean;
  children?: ReactNode;
}) {
  const { userId, claim, release, finish } = useFeatureLearning();
  const id = useId();
  const region = useRef<HTMLDivElement>(null);
  const target = useRef<HTMLDivElement>(null);
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number; side: string; arrow: number; pointer: boolean; anchorLeft: number; anchorTop: number; anchorWidth: number; anchorHeight: number } | null>(null);
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const attempted = useRef(false);
  const key = `${userId ?? 'anonymous'}:${lesson}`;

  const focusTarget = useCallback(() => {
    const selectors = ['[contenteditable="true"]', 'textarea:not(:disabled)', 'input:not(:disabled):not([type="hidden"]):not([type="file"])', 'select:not(:disabled)', '[role="switch"]:not(:disabled)', '[role="slider"]', 'button:not(:disabled)', '[tabindex="0"]'];
    const control = selectors.map(selector => target.current?.querySelector<HTMLElement>(selector)).find(Boolean);
    (control ?? region.current)?.focus({ preventScroll: true });
  }, []);

  const close = useCallback((restoreFocus = true, outcome: FeatureLessonOutcome = 'dismissed') => {
    finish(lesson, outcome);
    setOpen(false);
    release(id);
    if (restoreFocus && panel?.contains(document.activeElement)) focusTarget();
  }, [lesson, finish, id, release, panel, focusTarget]);

  useLayoutEffect(() => {
    attempted.current = false;
    setOpen(false);
    return () => release(id);
  }, [key, id, release]);
  useEffect(() => {
    if (!ready || !region.current || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(entries => setVisible(entries.some(entry => entry.isIntersecting)), { threshold: 0.25 });
    observer.observe(region.current);
    return () => observer.disconnect();
  }, [ready]);
  useEffect(() => {
    if (!ready || !userId) return undefined;
    if (engaged) {
      finish(lesson, 'used');
      release(id);
      setOpen(false);
      attempted.current = true;
      return undefined;
    }
    if (!visible || attempted.current) return undefined;
    attempted.current = true;
    let cancelled = false;
    void claim(id, lesson).then(allowed => {
      if (!cancelled && allowed) setOpen(true);
    });
    return () => { cancelled = true; release(id); };
  }, [ready, engaged, visible, userId, key, id, lesson, claim, release, finish]);
  useEffect(() => {
    if (!ready) { release(id); setOpen(false); }
  }, [ready, id, release]);
  useEffect(() => {
    if (open && !visible) close();
  }, [open, visible, close]);

  // A portal escapes clipping in editors and registers with the surrounding dialog's focus scope.
  useLayoutEffect(() => {
    const anchor = children ? target.current : region.current;
    if (!open || !panel || !anchor) return undefined;
    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const gap = 12;
      const edge = 16;
      const width = panel.offsetWidth;
      const height = panel.offsetHeight;
      const viewport = window.visualViewport;
      const viewWidth = viewport?.width ?? window.innerWidth;
      const viewHeight = viewport?.height ?? window.innerHeight;
      const originX = viewport?.offsetLeft ?? 0;
      const originY = viewport?.offsetTop ?? 0;
      const right = originX + viewWidth - edge;
      const bottom = originY + viewHeight - edge;
      let left = rect.right - width;
      let top = rect.bottom + gap;
      let side = 'bottom';
      if (rect.right + gap + width <= right) {
        left = rect.right + gap;
        top = rect.top;
        side = 'right';
      } else if (rect.left - gap - width >= originX + edge) {
        left = rect.left - gap - width;
        top = rect.top;
        side = 'left';
      } else if (top + height > bottom && rect.top - gap - height >= originY + edge) {
        top = rect.top - gap - height;
        side = 'top';
      }
      left = Math.max(originX + edge, Math.min(left, right - width));
      top = Math.max(originY + edge, Math.min(top, bottom - height));
      // Clamping can cover a large target. Point toward its visible part, never empty space.
      let pointer = true;
      const overlaps = left < rect.right && left + width > rect.left && top < rect.bottom && top + height > rect.top;
      if (overlaps) {
        if (rect.top < top) side = 'bottom';
        else if (rect.bottom > top + height) side = 'top';
        else if (rect.left < left) side = 'right';
        else if (rect.right > left + width) side = 'left';
        else pointer = false;
      }
      const vertical = side === 'left' || side === 'right';
      const arrow = Math.max(24, Math.min((vertical ? height : width) - 24, vertical ? rect.top + rect.height / 2 - top : rect.left + rect.width / 2 - left));
      const next = { left, top, side, arrow, pointer, anchorLeft: rect.left - 6, anchorTop: rect.top - 6, anchorWidth: rect.width + 12, anchorHeight: rect.height + 12 };
      setPosition(previous => previous && Object.keys(next).every(key => previous[key as keyof typeof next] === next[key as keyof typeof next]) ? previous : next);
    };
    let motionFrame: number | null = null;
    /** Follow a sliding ancestor only while its animation can move the target. */
    const trackMotion = () => {
      motionFrame = null;
      place();
      for (let ancestor: Element | null = anchor; ancestor; ancestor = ancestor.parentElement) {
        if (ancestor.getAnimations?.().some(animation => animation.playState === 'running')) {
          motionFrame = requestAnimationFrame(trackMotion);
          break;
        }
      }
    };
    /** CSS transforms do not notify ResizeObserver, including settings-panel entry. */
    const followAncestorMotion = (event: Event) => {
      if (!(event.target instanceof Element) || !event.target.contains(anchor)) return;
      if (motionFrame !== null) cancelAnimationFrame(motionFrame);
      motionFrame = requestAnimationFrame(trackMotion);
    };
    const motionEvents = ['transitionrun', 'transitionend', 'transitioncancel', 'animationstart', 'animationend', 'animationcancel'];
    motionEvents.forEach(event => document.addEventListener(event, followAncestorMotion, true));
    place();
    motionFrame = requestAnimationFrame(trackMotion);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    observer?.observe(anchor);
    observer?.observe(panel);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    return () => {
      observer?.disconnect();
      if (motionFrame !== null) cancelAnimationFrame(motionFrame);
      motionEvents.forEach(event => document.removeEventListener(event, followAncestorMotion, true));
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
    };
  }, [open, panel, children]);

  useEffect(() => {
    if (!open) return undefined;
    const dismissOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !panel?.contains(event.target)) close(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    document.addEventListener('pointerdown', dismissOutside, true);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('pointerdown', dismissOutside, true);
      document.removeEventListener('keydown', escape, true);
    };
  }, [open, panel, close]);

  /** Continue directly in the live control; optional learning never enables a paid feature. */
  const continueWorking = () => {
    close(false, 'completed');
    focusTarget();
  };

  return <div ref={region} className="feature-lesson" data-lesson={lesson} tabIndex={-1}>
    <div ref={target} className="feature-lesson-target">{children}</div>
    {ready && <Portal><Transition show={open} as={Fragment}>
      <div className="feature-lesson-layer" aria-hidden={!open} inert={!open} style={{ visibility: position ? 'visible' : 'hidden' }}>
        {position && <div className="feature-lesson-spotlight" aria-hidden="true" style={{ left: position.anchorLeft, top: position.anchorTop, width: position.anchorWidth, height: position.anchorHeight }} />}
        <div ref={setPanel} id={id} role="dialog" aria-modal="false" aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}
          className="feature-lesson-card" data-theme="dark" data-placement={position?.side}
          style={{ left: position?.left ?? 16, top: position?.top ?? 16 }}>
          {position?.pointer && <span className="feature-lesson-pointer" aria-hidden="true" style={position.side === 'left' || position.side === 'right' ? { top: position.arrow } : { left: position.arrow }} />}
          <div className="feature-lesson-surface"><LessonVideo lesson={lesson} active={visible && open} onDone={continueWorking} onClose={() => close()} id={id} /></div>
        </div>
      </div>
    </Transition></Portal>}
  </div>;
}
